import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faExternalLinkAlt,
    faEdit,
    faPaperPlane,
    faArchive,
    faImage,
    faSpinner,
    faXmark,
    faChevronLeft,
    faChevronRight,
} from '@fortawesome/free-solid-svg-icons';
import { ActionsDropdown, ActionsDropdownItem, ActionsDropdownDivider } from './ActionsDropdown';
import { TableEmptyState } from './TableEmptyState';
import { useAppContext } from '../../context/AppContext';
import { updateData } from '../../services';
import { appendUploadsFromResults, fetchUploadBlobObjectUrl, isImageUpload } from '../../utils/uploads';
import { getAssociatedDesignId } from '../../utils/openUploadFile';
import { NewFileForm, EditUploadForm, SendUploadForm, ArchiveUploadForm } from '../forms';

const PRINTED_COLUMN_EMAILS = new Set(['gillin@mtsolar.us', 'developer@mtsolar.us']);

const UploadLightbox = ({ images, startIndex, blobCache, onCache, onClose }) => {
    const { token, base_url } = useAppContext();
    const [index, setIndex] = useState(startIndex);
    const [status, setStatus] = useState('idle');

    const current = images[index];
    const src = current ? blobCache[current.id] : null;

    useEffect(() => {
        setIndex(startIndex);
    }, [startIndex]);

    useEffect(() => {
        if (!current?.id || !token) return;

        if (blobCache[current.id]) {
            setStatus('loaded');
            return;
        }

        const controller = new AbortController();
        setStatus('loading');

        fetchUploadBlobObjectUrl(current.id, token, base_url, controller.signal)
            .then((url) => {
                onCache(current.id, url);
                setStatus('loaded');
            })
            .catch((err) => {
                if (err.name !== 'AbortError') {
                    setStatus('error');
                }
            });

        return () => controller.abort();
    }, [index, current?.id, token, base_url, blobCache, onCache]);

    const prev = useCallback(
        () => setIndex((i) => (i - 1 + images.length) % images.length),
        [images.length]
    );
    const next = useCallback(
        () => setIndex((i) => (i + 1) % images.length),
        [images.length]
    );

    useEffect(() => {
        const handler = (e) => {
            if (e.key === 'Escape') onClose();
            if (e.key === 'ArrowLeft') prev();
            if (e.key === 'ArrowRight') next();
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [onClose, prev, next]);

    if (!current) return null;

    return createPortal(
        <div className="lightbox-backdrop" onClick={onClose}>
            <div className="lightbox-container" onClick={(e) => e.stopPropagation()}>
                <button type="button" className="lightbox-close" onClick={onClose} aria-label="Close gallery">
                    <FontAwesomeIcon icon={faXmark} />
                </button>

                <div className="lightbox-image-area">
                    {images.length > 1 && (
                        <button type="button" className="lightbox-nav lightbox-prev" onClick={prev} aria-label="Previous image">
                            <FontAwesomeIcon icon={faChevronLeft} />
                        </button>
                    )}

                    <div className="lightbox-image-wrapper">
                        {status === 'loading' && (
                            <div className="lightbox-loading">
                                <FontAwesomeIcon icon={faSpinner} spin />
                            </div>
                        )}
                        {status === 'error' && (
                            <div className="lightbox-loading">
                                <FontAwesomeIcon icon={faImage} />
                                <p>Failed to load image</p>
                            </div>
                        )}
                        {status === 'loaded' && src && (
                            <img src={src} alt={current.upload_name} className="lightbox-img" />
                        )}
                    </div>

                    {images.length > 1 && (
                        <button type="button" className="lightbox-nav lightbox-next" onClick={next} aria-label="Next image">
                            <FontAwesomeIcon icon={faChevronRight} />
                        </button>
                    )}
                </div>

                <div className="lightbox-footer">
                    <span className="lightbox-filename">{current.upload_name}</span>
                    {images.length > 1 && (
                        <span className="lightbox-counter">{index + 1} / {images.length}</span>
                    )}
                </div>
            </div>

            <style>{`
                .lightbox-backdrop {
                    position: fixed;
                    inset: 0;
                    background: rgba(0, 0, 0, 0.88);
                    z-index: 9999;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .lightbox-container {
                    position: relative;
                    display: flex;
                    flex-direction: column;
                    max-width: min(90vw, 1000px);
                    max-height: 90vh;
                    width: 100%;
                }
                .lightbox-close {
                    position: absolute;
                    top: -40px;
                    right: 0;
                    background: none;
                    border: none;
                    color: #fff;
                    font-size: 1.5rem;
                    cursor: pointer;
                    padding: 0.25rem 0.5rem;
                    opacity: 0.8;
                }
                .lightbox-close:hover { opacity: 1; }
                .lightbox-image-area {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }
                .lightbox-nav {
                    flex-shrink: 0;
                    background: rgba(255,255,255,0.12);
                    border: none;
                    color: #fff;
                    font-size: 1.25rem;
                    cursor: pointer;
                    padding: 0.75rem 0.9rem;
                    border-radius: 6px;
                }
                .lightbox-nav:hover { background: rgba(255,255,255,0.25); }
                .lightbox-image-wrapper {
                    flex: 1;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    min-height: 200px;
                    max-height: calc(90vh - 80px);
                }
                .lightbox-img {
                    max-width: 100%;
                    max-height: calc(90vh - 80px);
                    object-fit: contain;
                    border-radius: 4px;
                    display: block;
                }
                .lightbox-loading {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 0.75rem;
                    color: rgba(255,255,255,0.6);
                    font-size: 2rem;
                }
                .lightbox-loading p { font-size: 0.9rem; margin: 0; }
                .lightbox-footer {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    margin-top: 0.75rem;
                    padding: 0 0.25rem;
                }
                .lightbox-filename {
                    color: rgba(255,255,255,0.75);
                    font-size: 0.85rem;
                    word-break: break-all;
                    flex: 1;
                    margin-right: 1rem;
                }
                .lightbox-counter {
                    color: rgba(255,255,255,0.5);
                    font-size: 0.85rem;
                    white-space: nowrap;
                }
            `}</style>
        </div>,
        document.body
    );
};

export const UploadsTable = ({ project, files, setFiles, designs = [] }) => {
    const { token, base_url, user, setProjects } = useAppContext();
    const [editingUpload, setEditingUpload] = useState(null);
    const [sendingUpload, setSendingUpload] = useState(null);
    const [archivingUpload, setArchivingUpload] = useState(null);
    const [openDropdownId, setOpenDropdownId] = useState(null);
    const [printedUpdatingId, setPrintedUpdatingId] = useState(null);
    const [lightboxIndex, setLightboxIndex] = useState(null);
    const [blobCache, setBlobCache] = useState({});
    const blobCacheRef = useRef({});

    const galleryImages = useMemo(
        () =>
            [...files]
                .filter(isImageUpload)
                .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)),
        [files]
    );

    const quotedDesignIds = useMemo(
        () => new Set(designs.filter((d) => d.is_quoted).map((d) => String(d.id))),
        [designs]
    );

    const isQuotedBom = useCallback(
        (file) => {
            if (!file?.is_bom) return false;
            const designId = getAssociatedDesignId(file);
            return designId != null && quotedDesignIds.has(String(designId));
        },
        [quotedDesignIds]
    );

    const handleBlobCached = useCallback((fileId, url) => {
        blobCacheRef.current[fileId] = url;
        setBlobCache((prev) => ({ ...prev, [fileId]: url }));
    }, []);

    const openLightbox = useCallback(
        (file) => {
            const idx = galleryImages.findIndex((f) => f.id === file.id);
            if (idx !== -1) {
                setLightboxIndex(idx);
            }
        },
        [galleryImages]
    );

    useEffect(() => {
        const cache = blobCacheRef.current;
        return () => {
            Object.values(cache).forEach((url) => URL.revokeObjectURL(url));
        };
    }, []);

    const setDropdownOpen = useCallback((fileId, open) => {
        setOpenDropdownId(open ? fileId : null);
    }, []);

    const showPrintedColumn =
        user?.email && PRINTED_COLUMN_EMAILS.has(user.email.toLowerCase());

    const manufacturingHoldRestricted =
        showPrintedColumn && !!project?.manufacturing_hold;

    const isTravelerViewBlocked = (file) =>
        manufacturingHoldRestricted && file.is_traveler;

    const handlePrintedToggle = async (file, isPrinted) => {
        if (project?.manufacturing_hold) return;
        if (printedUpdatingId != null) return;
        setPrintedUpdatingId(file.id);
        try {
            const response = await updateData(token, `${base_url}/apis/uploads/${file.id}`, {
                is_printed: isPrinted,
            });
            if (response?.upload) {
                setFiles((prev) => prev.map((f) => (f.id === file.id ? response.upload : f)));

                if (isPrinted && project?.current_stage === 'prepare to build') {
                    try {
                        const updatedProject = await updateData(
                            token,
                            `${base_url}/apis/projects/${project.id}`,
                            { current_stage: 'manufacturing' }
                        );
                        if (updatedProject?.project) {
                            setProjects((prev) =>
                                prev.map((p) => (p.id === project.id ? updatedProject.project : p))
                            );
                        } else {
                            window.showAlert(
                                500,
                                'Printed status saved, but stage update failed: ' +
                                    (updatedProject?.msg || 'Unknown error')
                            );
                        }
                    } catch (stageErr) {
                        console.error('Advance project stage after print error:', stageErr);
                        window.showAlert(
                            500,
                            'Printed status saved, but failed to advance project to manufacturing.'
                        );
                    }
                }
            } else {
                window.showAlert(500, 'Failed to update: ' + (response?.msg || 'Unknown error'));
            }
        } catch (err) {
            console.error('Update is_printed error:', err);
            window.showAlert(500, 'Failed to update printed status.');
        } finally {
            setPrintedUpdatingId(null);
        }
    };

    const handleUploadSuccess = (results) => {
        setFiles((prev) => appendUploadsFromResults(prev, results));
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return 'N/A';
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();
        return `${month}-${day}-${year}`;
    };

    // File types that should be downloaded rather than opened in a new tab
    const DOWNLOAD_ONLY_TYPES = ['heic', 'csv', 'xlsx', 'docx', 'dxf', 'step', 'stp'];

    const isDownloadOnlyFile = (file) => {
        const extension = file.upload_name?.split('.').pop()?.toLowerCase();
        const uploadType = file.upload_type?.toLowerCase();
        return DOWNLOAD_ONLY_TYPES.some(type => 
            extension === type || uploadType?.includes(type)
        );
    };

    const downloadFile = async (file) => {
        if (isTravelerViewBlocked(file)) {
            window.showAlert(
                403,
                'This project has a manufacturing hold. Traveler files cannot be viewed until the hold is released.'
            );
            return;
        }

        try {
            const res = await fetch(`${base_url}/apis/uploads/${file.id}/file`, {
                headers: { Authorization: token },
            });
            if (!res.ok) {
                window.showAlert(500, "Unauthorized or error fetching file");
                return;
            }
            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = file.upload_name || 'download';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            window.showAlert(500, "Error fetching file");
        }
    };

    const openFile = async (file) => {
        if (!token) {
            window.showAlert(401, "Please login to view file");
            return;
        }

        if (isTravelerViewBlocked(file)) {
            window.showAlert(
                403,
                'This project has a manufacturing hold. Traveler files cannot be viewed until the hold is released.'
            );
            return;
        }

        // For download-only file types, trigger download without opening new tab
        if (isDownloadOnlyFile(file)) {
            await downloadFile(file);
            return;
        }

        // Open window immediately within user gesture context (required for iOS Safari)
        const newWindow = window.open('about:blank', '_blank');
        
        // If popup was blocked, fall back to current window navigation
        if (!newWindow) {
            try {
                const res = await fetch(`${base_url}/apis/uploads/${file.id}/file`, {
                    headers: { Authorization: token },
                });
                if (!res.ok) {
                    window.showAlert(500, "Unauthorized or error fetching file");
                    return;
                }
                const blob = await res.blob();
                const url = window.URL.createObjectURL(blob);
                window.location.href = url;
            } catch (error) {
                window.showAlert(500, "Error fetching file");
            }
            return;
        }

        try {
            const res = await fetch(`${base_url}/apis/uploads/${file.id}/file`, {
                headers: { Authorization: token },
            });

            if (!res.ok) {
                newWindow.close();
                window.showAlert(500, "Unauthorized or error fetching file");
                return;
            }

            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            newWindow.location.href = url;
        } catch (error) {
            newWindow.close();
            window.showAlert(500, "Error fetching file");
        }
    };

    return (
        <div>
            <NewFileForm project={project} onUploadSuccess={handleUploadSuccess} />
            <br />
            {manufacturingHoldRestricted && (
                <div className="banner banner--hold" role="alert">
                    This project has a manufacturing hold. Marking files as printed and viewing traveler
                    PDFs are disabled until the hold is released.
                </div>
            )}
            <div className="table-responsive table-responsive--scroll">
                {files.length === 0 ? (
                    <TableEmptyState>
                        <p>No files available for this project.</p>
                        <p>Click the "Upload File" button above to upload your first file.</p>
                    </TableEmptyState>
                ) : (
                    <table className="data-table uploads-table">
                        <thead>
                            <tr>
                                <th className="col-primary name-column">Name</th>
                                <th className="col-type hide-on-mobile">Type</th>
                                <th className="col-date hide-on-mobile">Date Created</th>
                                {showPrintedColumn && (
                                    <th className="col-check printed-column">
                                        Printed
                                    </th>
                                )}
                                <th className="col-actions">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(() => {
                                const sortByDate = (arr) => [...arr].sort((a, b) =>
                                    new Date(b.created_at || 0) - new Date(a.created_at || 0)
                                );

                                const nonBomFiles = files.filter(f => f.is_bom !== true);
                                const boms           = sortByDate(files.filter(f => f.is_bom === true));
                                const travelers      = sortByDate(nonBomFiles.filter(f => f.is_traveler));
                                const packaging      = sortByDate(nonBomFiles.filter(f => !f.is_traveler && f.is_packaging_photo));
                                const skyciv         = sortByDate(nonBomFiles.filter(f => !f.is_traveler && !f.is_packaging_photo && f.upload_name?.endsWith('SkyCiv PDF.pdf')));
                                const others         = sortByDate(nonBomFiles.filter(f => !f.is_traveler && !f.is_packaging_photo && !f.upload_name?.endsWith('SkyCiv PDF.pdf')));

                                const allSections = [
                                    { label: 'Travelers',        files: travelers, superAdminOnly: true },
                                    { label: 'Packaging Photos', files: packaging, superAdminOnly: true },
                                    { label: 'BOMs',             files: boms,      superAdminOnly: true },
                                    { label: 'SkyCiv PDFs',      files: skyciv,   superAdminOnly: false },
                                    { label: 'Other Files',      files: others,   superAdminOnly: false },
                                ];
                                const sections = allSections
                                    .filter(s => s.files.length > 0)
                                    .filter(s => !s.superAdminOnly || user?.is_super_admin === true);

                                const renderRow = (file) => (
                                    <tr
                                        key={file.id}
                                        className={isQuotedBom(file) ? 'table-row--highlighted' : undefined}
                                    >
                                        <td className="col-primary name-column">
                                            {isImageUpload(file) ? (
                                                <button
                                                    type="button"
                                                    className="upload-gallery-link"
                                                    onClick={() => openLightbox(file)}
                                                    title="View in gallery"
                                                >
                                                    {file.upload_name}
                                                </button>
                                            ) : (
                                                file.upload_name
                                            )}
                                        </td>
                                        <td className="col-type hide-on-mobile">{file.upload_type}</td>
                                        <td className="col-date hide-on-mobile">{formatDate(file.created_at)}</td>
                                        {showPrintedColumn && (
                                            <td className="col-check printed-column">
                                                <input
                                                    type="checkbox"
                                                    checked={!!file.is_printed}
                                                    disabled={
                                                        printedUpdatingId === file.id ||
                                                        manufacturingHoldRestricted
                                                    }
                                                    onChange={(e) =>
                                                        handlePrintedToggle(file, e.target.checked)
                                                    }
                                                    title={
                                                        manufacturingHoldRestricted
                                                            ? 'Manufacturing hold — cannot change printed status'
                                                            : 'Printed'
                                                    }
                                                    aria-label={`Printed: ${file.upload_name || file.id}`}
                                                />
                                            </td>
                                        )}
                                        <td className="col-actions">
                                            <ActionsDropdown
                                                isOpen={openDropdownId === file.id}
                                                onOpenChange={(open) => setDropdownOpen(file.id, open)}
                                                portal
                                                menuWidth={250}
                                            >
                                                <ActionsDropdownItem>
                                                    <button
                                                        onClick={() => {
                                                            openFile(file);
                                                            setOpenDropdownId(null);
                                                        }}
                                                        className="btn btn-action btn-primary"
                                                        disabled={isTravelerViewBlocked(file)}
                                                        title={
                                                            isTravelerViewBlocked(file)
                                                                ? 'Manufacturing hold — traveler files cannot be viewed'
                                                                : 'Open file'
                                                        }
                                                    >
                                                        <FontAwesomeIcon icon={faExternalLinkAlt} /> Open File
                                                    </button>
                                                </ActionsDropdownItem>
                                                {isImageUpload(file) && galleryImages.length > 0 && (
                                                    <ActionsDropdownItem onClick={() => setOpenDropdownId(null)}>
                                                        <button
                                                            type="button"
                                                            onClick={() => openLightbox(file)}
                                                            className="btn btn-secondary btn-action"
                                                        >
                                                            <FontAwesomeIcon icon={faImage} /> View in gallery
                                                        </button>
                                                    </ActionsDropdownItem>
                                                )}
                                                <ActionsDropdownItem onClick={() => setOpenDropdownId(null)}>
                                                    <button
                                                        type="button"
                                                        onClick={() => setSendingUpload(file)}
                                                        className="btn btn-secondary btn-action"
                                                        title="Send via email"
                                                    >
                                                        <FontAwesomeIcon icon={faPaperPlane} /> Send
                                                    </button>
                                                </ActionsDropdownItem>
                                                <ActionsDropdownDivider />
                                                <ActionsDropdownItem onClick={() => setOpenDropdownId(null)}>
                                                    <button onClick={() => setEditingUpload(file)} className="btn btn-secondary btn-action">
                                                        <FontAwesomeIcon icon={faEdit} /> Edit
                                                    </button>
                                                </ActionsDropdownItem>
                                                {!file.is_archived && (
                                                    <ActionsDropdownItem onClick={() => setOpenDropdownId(null)}>
                                                        <button
                                                            type="button"
                                                            onClick={() => setArchivingUpload(file)}
                                                            className="btn btn-action btn-danger"
                                                            title="Archive upload"
                                                        >
                                                            <FontAwesomeIcon icon={faArchive} /> Archive
                                                        </button>
                                                    </ActionsDropdownItem>
                                                )}
                                            </ActionsDropdown>
                                        </td>
                                    </tr>
                                );

                                return sections.map(section => (
                                    <>
                                        <tr key={`section-${section.label}`} className="section-header-row">
                                            <td colSpan={showPrintedColumn ? 5 : 4}>{section.label}</td>
                                        </tr>
                                        {section.files.map(renderRow)}
                                    </>
                                ));
                            })()}
                        </tbody>
                    </table>
                )}
            </div>


            {editingUpload && (
                <EditUploadForm
                    upload={editingUpload}
                    setFiles={setFiles}
                    onClose={() => setEditingUpload(null)}
                />
            )}
            {sendingUpload && (
                <SendUploadForm upload={sendingUpload} onClose={() => setSendingUpload(null)} />
            )}
            {archivingUpload && (
                <ArchiveUploadForm
                    upload={archivingUpload}
                    setFiles={setFiles}
                    onClose={() => setArchivingUpload(null)}
                />
            )}
            {lightboxIndex !== null && galleryImages.length > 0 && (
                <UploadLightbox
                    images={galleryImages}
                    startIndex={lightboxIndex}
                    blobCache={blobCache}
                    onCache={handleBlobCached}
                    onClose={() => setLightboxIndex(null)}
                />
            )}
        </div>
    );
}